import { NavLink, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { IS_ADMIN_HOST } from "@/lib/auth";
import { useAuth } from "@/stores/auth";
import type { DataHandlingPosture } from "@/lib/types";

// Banner stays yellow until 12 months stale, then escalates color. The
// 12mo cutoff matches plan KD2's trip-wire spec — Anthropic data-handling
// terms can shift, so an admin re-confirms once a year.
const STALE_THRESHOLD_MS = 12 * 30 * 24 * 60 * 60 * 1000;

function posturePresentation(p: DataHandlingPosture | undefined): {
  show: boolean;
  variant: "red" | "yellow";
  message: string;
} {
  if (!p || p.posture === "unverified") {
    return {
      show: true,
      variant: "red",
      message:
        "Anthropic data-handling posture is unverified — venue-document uploads disabled.",
    };
  }
  const verifiedAtMs = p.verified_at ? new Date(p.verified_at).getTime() : NaN;
  const isStale =
    !Number.isFinite(verifiedAtMs) ||
    Date.now() - verifiedAtMs > STALE_THRESHOLD_MS;
  if (isStale) {
    return {
      show: true,
      variant: "yellow",
      message:
        "Anthropic data-handling posture verification is stale (> 12 months). An admin should re-confirm.",
    };
  }
  return { show: false, variant: "yellow", message: "" };
}

function DataHandlingBanner() {
  const capabilities = useAuth((s) => s.capabilities);
  if (!IS_ADMIN_HOST) return null;
  const posture = capabilities?.data_handling;
  const { show, variant, message } = posturePresentation(posture);
  if (!show) return null;
  const cls =
    variant === "red"
      ? "bg-destructive/15 border-destructive/40 text-destructive"
      : "bg-yellow-500/15 border-yellow-500/40 text-yellow-700";
  return (
    <div className={`border-b px-4 py-2 text-xs sm:px-6 ${cls}`}>
      <span className="font-medium">{message}</span>{" "}
      Contact your system administrator to run{" "}
      <code className="rounded bg-background/40 px-1 py-0.5 font-mono">
        python3 /opt/ovr/cloud/services/api/tools/set_data_handling_posture.py
      </code>{" "}
      on the cloud-api host.{" "}
      {posture?.anthropic_terms_reference_url && (
        <a
          href={posture.anthropic_terms_reference_url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          What this means →
        </a>
      )}
    </div>
  );
}

// Top-of-page nav shell. Admin-only entries are gated by IS_ADMIN_HOST.
// The window picker is query-param driven (?window=...) so deep links work.

const clientLinks = [
  { to: "/", label: "Dashboard" },
  { to: "/feed", label: "Event Feed" },
];

const adminLinks = [
  { to: "/admin/load-in", label: "Load-in" },
  { to: "/admin/venues", label: "Venues" },
  { to: "/admin/feeds", label: "House Feeds" },
  { to: "/admin/rules", label: "Rules" },
  { to: "/admin/pq-index", label: "PQ Index" },
];

function linkClass({ isActive }: { isActive: boolean }) {
  return cn(
    "px-3 py-2 text-sm rounded-md transition-colors",
    isActive
      ? "bg-secondary text-secondary-foreground"
      : "text-muted-foreground hover:text-foreground hover:bg-accent"
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const role = useAuth((s) => s.role);
  const [sp] = useSearchParams();
  const activeWindow = sp.get("window");

  return (
    <div className="min-h-screen flex flex-col">
      <DataHandlingBanner />
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="px-4 sm:px-6 flex h-14 items-center gap-4">
          <div className="font-semibold tracking-tight">
            Dyno <span className="text-muted-foreground font-normal">— Power Quality</span>
          </div>
          <nav className="flex items-center gap-1">
            {clientLinks.map((l) => (
              <NavLink key={l.to} to={appendWindow(l.to, activeWindow)} end className={linkClass}>
                {l.label}
              </NavLink>
            ))}
            {IS_ADMIN_HOST &&
              adminLinks.map((l) => (
                <NavLink key={l.to} to={appendWindow(l.to, activeWindow)} className={linkClass}>
                  {l.label}
                </NavLink>
              ))}
          </nav>
          <div className="ml-auto text-xs text-muted-foreground">
            {IS_ADMIN_HOST ? "admin" : "client"} · {role}
          </div>
        </div>
      </header>
      <main className="flex-1 px-4 sm:px-6 py-6">{children}</main>
      <footer className="border-t py-3 text-xs text-muted-foreground">
        <div className="px-4 sm:px-6">
          Overdrive Energy Solutions · Dyno v0.1
        </div>
      </footer>
    </div>
  );
}

function appendWindow(path: string, window: string | null): string {
  if (!window) return path;
  return `${path}?window=${encodeURIComponent(window)}`;
}
